import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useFriends, useFriendRequests, useFriendSearch, useSendFriendRequest, useRespondToFriendRequest, useRemoveFriend } from '../hooks/useGame';
import AnimatedTabs from './AnimatedTabs';
import './ProfileModal.css';
import './FriendsModal.css';

interface FriendsModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export default function FriendsModal({ isOpen, onClose }: FriendsModalProps) {
    const [activeTab, setActiveTab] = useState<'friends' | 'add' | 'requests'>('friends');
    const [searchQuery, setSearchQuery] = useState('');

    const { data: friends, isLoading: isLoadingFriends } = useFriends();
    const { data: requests, isLoading: isLoadingRequests } = useFriendRequests();
    const { data: searchResults, isLoading: isSearching } = useFriendSearch(searchQuery);

    const { mutate: sendRequest } = useSendFriendRequest();
    const { mutate: respondRequest } = useRespondToFriendRequest();
    const { mutate: removeFriend } = useRemoveFriend();

    const pendingCount = requests?.length || 0;

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="profile-modal-overlay" onClick={onClose}>
                    <motion.div
                        className="profile-modal friends-modal"
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        onClick={e => e.stopPropagation()}
                    >
                        <div className="profile-modal-header">
                            <h2 className="profile-modal-title">Friends</h2>
                            <button className="profile-modal-close" onClick={onClose}>×</button>
                        </div>

                        <AnimatedTabs
                            tabs={[
                                { id: 'friends' as const, label: 'My Friends' },
                                { id: 'requests' as const, label: 'Requests', badge: pendingCount },
                                { id: 'add' as const, label: 'Add Friend' },
                            ]}
                            activeTab={activeTab}
                            onTabChange={setActiveTab}
                            layoutId="friends-tabs"
                        />

                        <div className="inbox-content">
                            {activeTab === 'friends' && (
                                <div className="friends-list">
                                    {isLoadingFriends ? (
                                        <div className="loading-spinner">Loading friends...</div>
                                    ) : friends?.length === 0 ? (
                                        <div className="empty-state">
                                            <p>You haven't added any friends yet.</p>
                                        </div>
                                    ) : (
                                        friends?.map(friend => (
                                            <div key={friend.id} className="friend-item">
                                                <img src={`/static/avatars/${friend.avatar || 'default.png'}`} className="friend-avatar" alt={friend.username} />
                                                <div className="friend-info">
                                                    <span className="friend-name">{friend.username}</span>
                                                </div>
                                                <button className="action-btn danger" onClick={() => removeFriend(friend.id)}>
                                                    Remove
                                                </button>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === 'requests' && (
                                <div className="friends-list">
                                    {isLoadingRequests ? (
                                        <div className="loading-spinner">Loading requests...</div>
                                    ) : requests?.length === 0 ? (
                                        <div className="empty-state">
                                            <p>No pending requests.</p>
                                        </div>
                                    ) : (
                                        requests?.map(req => (
                                            <div key={req.id} className="friend-item">
                                                {/* req structure depends on API, assume it has sender info */}
                                                <img src={`/static/avatars/${req.sender_avatar || 'default.png'}`} className="friend-avatar" alt={req.sender_username} />
                                                <div className="friend-info">
                                                    <span className="friend-name">{req.sender_username}</span>
                                                    <span className="friend-status">sent you a request</span>
                                                </div>
                                                <div className="friend-actions">
                                                    <button className="action-btn primary" onClick={() => respondRequest({ requestId: req.id, accept: true })}>
                                                        Accept
                                                    </button>
                                                    <button className="action-btn secondary" onClick={() => respondRequest({ requestId: req.id, accept: false })}>
                                                        Decline
                                                    </button>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                            )}

                            {activeTab === 'add' && (
                                <div>
                                    <div className="search-bar">
                                        <input
                                            type="text"
                                            className="search-input"
                                            placeholder="Search users..."
                                            value={searchQuery}
                                            onChange={e => setSearchQuery(e.target.value)}
                                            autoFocus
                                        />
                                    </div>

                                    <div className="friends-list">
                                        {searchQuery.length < 2 ? (
                                            <p className="auth-note">Type at least 2 characters to search.</p>
                                        ) : isSearching ? (
                                            <div className="loading-spinner">Searching...</div>
                                        ) : searchResults?.length === 0 ? (
                                            <p className="auth-note">No users found.</p>
                                        ) : (
                                            searchResults?.map(user => (
                                                <div key={user.id} className="friend-item">
                                                    <img src={`/static/avatars/${user.avatar || 'default.png'}`} className="friend-avatar" alt={user.username} />
                                                    <div className="friend-info">
                                                        <span className="friend-name">{user.username}</span>
                                                        <span className="friend-status">
                                                            {user.friendship_status === 'friends' ? 'Already friends' :
                                                                user.friendship_status === 'pending_sent' ? 'Request sent' :
                                                                    user.friendship_status === 'pending_received' ? 'Request received' : ''}
                                                        </span>
                                                    </div>
                                                    {user.friendship_status === 'none' && (
                                                        <button className="action-btn primary" onClick={() => sendRequest(user.id)}>
                                                            Add
                                                        </button>
                                                    )}
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
